// src/components/PresentationDisplay.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideInLeft = keyframes`
  from { transform: translateX(-20px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const slideInRight = keyframes`
  from { transform: translateX(20px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

// Main container
const PresentationWrapper = styled.div`
  margin: ${({ theme }) => theme.spacing.xl} 0;
  width: 100%;
`;

const PresentationContainer = styled.div`
  position: relative;
  width: 100%;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  
  /* Dynamic aspect ratio based on embed height */
  &::before {
    content: "";
    display: block;
    padding-top: ${({ $aspectRatio }) => {
      switch($aspectRatio) {
        case 'tall': return '75%';      // 4:3 aspect ratio
        case 'square': return '100%';    // 1:1 aspect ratio
        case 'portrait': return '177.78%'; // 9:16 aspect ratio
        case 'standard':
        default: return '56.25%';        // 16:9 aspect ratio
      }
    }};
  }
`;

const SlideContent = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.background};
`;

// Navigation controls
const NavigationContainer = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  pointer-events: none;
  z-index: 10;
  padding: ${({ theme }) => theme.spacing.md};
`;

const NavButton = styled.button`
  pointer-events: all;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.surface};
  border: 2px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  opacity: ${({ disabled }) => disabled ? 0.3 : 0.8};
  backdrop-filter: blur(10px);
  
  &:hover:not(:disabled) {
    opacity: 1;
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.background};
    transform: scale(1.1);
  }
  
  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.focusRing};
    outline-offset: 2px;
  }
  
  &:disabled {
    cursor: not-allowed;
  }
  
  svg {
    width: 24px;
    height: 24px;
  }
  
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    
    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

// Slide counter and controls
const ControlBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const SlideCounter = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: 500;
`;

const ControlButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const ControlButton = styled.button`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.background};
  }
  
  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.focusRing};
    outline-offset: 2px;
  }
`;

// Slide image with loading state
const SlideImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  animation: ${({ $direction }) => 
    $direction === 'next' ? slideInRight : 
    $direction === 'prev' ? slideInLeft : 
    fadeIn} 0.3s ${({ theme }) => theme.transitions.easeOut};
`;

// PDF viewer
const PDFViewer = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

// Google Slides embed
const GoogleSlidesEmbed = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

// Loading skeleton
const LoadingSkeleton = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.surface} 0%,
    ${({ theme }) => theme.colors.border} 50%,
    ${({ theme }) => theme.colors.surface} 100%
  );
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  
  @keyframes loading {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

// Thumbnail strip for quick navigation
const ThumbnailStrip = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.md};
  overflow-x: auto;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  
  &::-webkit-scrollbar {
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.border};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.primary};
    border-radius: 3px;
  }
`;

const Thumbnail = styled.button`
  flex-shrink: 0;
  width: 80px;
  height: 45px;
  border: 2px solid ${({ $active, theme }) => 
    $active ? theme.colors.primary : theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  overflow: hidden;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  background: ${({ theme }) => theme.colors.background};
  padding: 0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    transform: scale(1.05);
  }
  
  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.focusRing};
    outline-offset: 2px;
  }
`;

const Caption = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  margin-top: ${({ theme }) => theme.spacing.md};
  font-style: italic;
`;

const PresentationDisplay = ({ 
  type = 'images', // 'pdf', 'images', 'google_slides', 'embed'
  slides = [],
  pdfSrc,
  googleSlidesUrl,
  embedUrl,
  embedHeight = 'standard', // 'standard', 'tall', 'square', 'portrait'
  title,
  caption,
  allowDownload = false,
  showThumbnails = false,
  autoPlay = false,
  autoPlayInterval = 5000
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [slideDirection, setSlideDirection] = useState(null);
  const containerRef = useRef(null);
  const autoPlayRef = useRef(null);

  // Calculate total slides based on type
  const totalSlides = type === 'images' ? slides.length : 1;

  // Navigation functions
  const goToSlide = useCallback((index) => {
    if (index >= 0 && index < totalSlides) {
      setSlideDirection(index > currentSlide ? 'next' : 'prev');
      setCurrentSlide(index);
    }
  }, [currentSlide, totalSlides]);

  const goToPrevious = useCallback(() => {
    goToSlide(currentSlide - 1);
  }, [currentSlide, goToSlide]);

  const goToNext = useCallback(() => {
    goToSlide((currentSlide + 1) % totalSlides);
  }, [currentSlide, totalSlides, goToSlide]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (type !== 'images') return;
      
      switch(e.key) {
        case 'ArrowLeft':
          goToPrevious();
          break;
        case 'ArrowRight':
          goToNext();
          break;
        case 'Home':
          goToSlide(0);
          break;
        case 'End':
          goToSlide(totalSlides - 1);
          break;
        case 'f':
        case 'F':
          toggleFullscreen();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [type, goToPrevious, goToNext, goToSlide, totalSlides]);

  // Auto-play functionality
  useEffect(() => {
    if (autoPlay && type === 'images' && totalSlides > 1) {
      autoPlayRef.current = setInterval(() => {
        goToNext();
      }, autoPlayInterval);

      return () => {
        if (autoPlayRef.current) {
          clearInterval(autoPlayRef.current);
        }
      };
    }
  }, [autoPlay, autoPlayInterval, goToNext, type, totalSlides]);

  // Pause auto-play on hover
  const handleMouseEnter = () => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
  };

  const handleMouseLeave = () => {
    if (autoPlay && type === 'images' && totalSlides > 1) {
      autoPlayRef.current = setInterval(() => {
        goToNext();
      }, autoPlayInterval);
    }
  };

  // Fullscreen functionality
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  // Touch/swipe support
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }
  };

  // Render content based on type
  const renderContent = () => {
    switch(type) {
      case 'pdf':
        return (
          <PDFViewer
            src={`${pdfSrc}#toolbar=0&navpanes=0&scrollbar=0`}
            title={title || 'PDF Presentation'}
            onLoad={() => setIsLoading(false)}
          />
        );

      case 'google_slides':
        return (
          <GoogleSlidesEmbed
            src={googleSlidesUrl}
            title={title || 'Google Slides Presentation'}
            allowFullScreen
            onLoad={() => setIsLoading(false)}
          />
        );

      case 'embed':
        return (
          <GoogleSlidesEmbed
            src={embedUrl}
            title={title || 'Embedded Presentation'}
            allowFullScreen
            onLoad={() => setIsLoading(false)}
          />
        );

      case 'images':
      default:
        if (slides.length === 0) return null;
        return (
          <>
            {isLoading && <LoadingSkeleton />}
            <SlideImage
              src={slides[currentSlide].image}
              alt={slides[currentSlide].alt || `Slide ${currentSlide + 1}`}
              onLoad={() => setIsLoading(false)}
              $direction={slideDirection}
            />
            {totalSlides > 1 && (
              <NavigationContainer>
                <NavButton
                  onClick={goToPrevious}
                  disabled={currentSlide === 0}
                  aria-label="Previous slide"
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d="M15 19l-7-7 7-7" 
                    />
                  </svg>
                </NavButton>
                <NavButton
                  onClick={goToNext}
                  disabled={currentSlide === totalSlides - 1}
                  aria-label="Next slide"
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d="M9 5l7 7-7 7" 
                    />
                  </svg>
                </NavButton>
              </NavigationContainer>
            )}
          </>
        );
    }
  };

  return (
    <PresentationWrapper>
      <PresentationContainer
        ref={containerRef}
        $aspectRatio={type === 'embed' ? embedHeight : 'standard'}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={type === 'images' ? onTouchStart : undefined}
        onTouchMove={type === 'images' ? onTouchMove : undefined}
        onTouchEnd={type === 'images' ? onTouchEnd : undefined}
      >
        <SlideContent>
          {renderContent()}
        </SlideContent>
      </PresentationContainer>

      {type === 'images' && totalSlides > 1 && (
        <ControlBar>
          <SlideCounter>
            Slide {currentSlide + 1} of {totalSlides}
          </SlideCounter>
          <ControlButtons>
            {autoPlay && (
              <ControlButton onClick={() => {}}>
                Pause
              </ControlButton>
            )}
            <ControlButton onClick={toggleFullscreen}>
              Fullscreen
            </ControlButton>
            {allowDownload && pdfSrc && (
              <ControlButton as="a" href={pdfSrc} download>
                Download
              </ControlButton>
            )}
          </ControlButtons>
        </ControlBar>
      )}

      {showThumbnails && type === 'images' && totalSlides > 1 && (
        <ThumbnailStrip>
          {slides.map((slide, index) => (
            <Thumbnail
              key={index}
              onClick={() => goToSlide(index)}
              $active={index === currentSlide}
              aria-label={`Go to slide ${index + 1}`}
            >
              <img src={slide.image} alt={`Thumbnail ${index + 1}`} />
            </Thumbnail>
          ))}
        </ThumbnailStrip>
      )}

      {caption && (
        <Caption>{caption}</Caption>
      )}
    </PresentationWrapper>
  );
};

export default PresentationDisplay;